import { api } from "../api/client.ts";
import type {
  ObjRule,
  QueueEntry,
  Queue as QueueOut,
  Queues,
  Reason,
  ReviewerQueue,
  SpaceOut,
  UserKind,
} from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { plain } from "../lib/commentText.ts";
import { conversationPath } from "../lib/conversation.ts";
import { scopeName, useEntities } from "../lib/data.ts";
import { formatRelative } from "../lib/format.ts";
import { spaceApi, spaceUrl } from "../lib/paths.ts";
import { displayName, nameOf, usePeople } from "../lib/people.ts";
import { type Resource, useResource } from "../lib/resource.ts";
import { location, setQuery, tabHref, tabParam } from "../lib/router.ts";
import { me, type PageProps } from "../lib/state.ts";
import { clip } from "../lib/text.ts";
import { titleOf } from "../lib/titles.ts";
import { useId } from "../lib/uid.ts";
import { Link } from "../ui/Button.tsx";
import { ItemCommentCount } from "../ui/counts.tsx";
import { Select } from "../ui/form.tsx";
import { Async, Badge, EmptyState } from "../ui/misc.tsx";
import { PageHeader } from "../ui/PageHeader.tsx";
import { LINK } from "../ui/styles.ts";
import { Table, Td, Th } from "../ui/Table.tsx";
import { TabNav } from "../ui/TabNav.tsx";

const QUEUE_LIMIT = 200;
/** The picker's value for every reviewer's queue at once. */
const EVERYONE = "everyone";
/** Each reason's tone (its name: `queue.reason.<reason>`). */
const REASON_TONE: Record<Reason, "blue" | "amber" | "gray"> = {
  new: "blue",
  stale: "amber",
  rule_changed: "gray",
  requested: "blue",
};

/** Someone's name, and what they are when they aren't a person. */
const withKind = (name: string, kind: UserKind) => `${name}${kind === "human" ? "" : ` (${t(`userKind.${kind}`)})`}`;

/** A reviewer's name (and kind), from the queues overview, else the members. */
function useReviewerName(reviewers: readonly ReviewerQueue[]): (id: string) => string {
  const members = usePeople();
  return (id) => {
    const r = reviewers.find((x) => x.user === id);
    return r ? withKind(displayName(r.user, r.name), r.kind) : nameOf(members, id);
  };
}

function Entry({ space, e, q, reason = true }: { space: SpaceOut; e: QueueEntry; q: Shown; reason?: boolean }) {
  return (
    <li class="flex flex-wrap items-center gap-3 px-4 py-3">
      <Link href={spaceUrl(space, `/items/${e.item.id}`)} class={LINK}>
        {titleOf(e.item)}
      </Link>
      <ItemCommentCount space={space} id={e.item.id} />
      {/* Everyone's queues at once: whose queue it's in. */}
      {q.whose ? (
        <span class="text-xs font-medium text-zinc-700">{t("queue.for", { name: q.whose(e.reviewer) })}</span>
      ) : null}
      {reason ? <Badge tone={REASON_TONE[e.reason]}>{t(`queue.reason.${e.reason}`)}</Badge> : null}
      <span class="text-xs text-zinc-500">
        {e.rules.map((id) => q.rules.find((r) => r.id === id)?.name ?? id).join(", ")}
      </span>
      <span class="ml-auto text-xs text-zinc-500">{formatRelative(e.item.updated_at)}</span>
      {e.requests.map((r) => (
        <p key={r.id} class="w-full text-sm text-zinc-700">
          {r.note
            ? t("queue.asked", { name: r.requested_by_name ?? r.requested_by, note: r.note })
            : t("queue.askedBare", { name: r.requested_by_name ?? r.requested_by })}
        </p>
      ))}
    </li>
  );
}

/** Whose queue: you, anyone with something to review here (with how much waits for them), or everyone's. */
function ReviewerPicker({ all, reviewer }: { all: Resource<Queues>; reviewer: string }) {
  const myId = me.value?.id ?? "";
  const rows = all.data?.data ?? [];
  const label = (name: string, kind: UserKind, size: number) => `${withKind(name, kind)} · ${size}`;
  const mine = rows.find((r) => r.user === myId);
  const id = useId();
  const options = [
    { value: myId, label: mine ? label(t("queue.you"), "human", mine.size) : t("queue.you") },
    ...rows.filter((r) => r.user !== myId).map((r) => ({ value: r.user, label: label(r.name, r.kind, r.size) })),
    { value: EVERYONE, label: t("queue.everyone") },
  ];
  return (
    <div class="flex max-w-full min-w-0 items-center gap-2">
      <label for={id} class="text-sm text-zinc-600">
        {t("queue.reviewer")}
      </label>
      <div class="w-80 min-w-0 max-w-full shrink">
        <Select
          id={id}
          value={reviewer}
          onValue={(v) => setQuery({ reviewer: v === myId ? null : v })}
          options={options}
        />
      </div>
    </div>
  );
}

/** The reviewer's rules: what they review, and how. */
function Rules({ space, rules }: { space: SpaceOut; rules: readonly ObjRule[] }) {
  const entities = useEntities(space);
  return (
    <Table stack={true}>
      <thead class="bg-zinc-50">
        <tr>
          <Th>{t("queue.rule")}</Th>
          <Th>{t("queue.appliesTo")}</Th>
          <Th>{t("queue.instructions")}</Th>
        </tr>
      </thead>
      <tbody class="divide-y divide-zinc-100">
        {rules.map((r) => (
          <tr key={r.id}>
            <Td>
              <Link href={spaceUrl(space, `/rules/${r.id}`)} class={LINK}>
                {r.name}
              </Link>
            </Td>
            <Td label={t("queue.appliesTo")}>{scopeName(entities.data, r)}</Td>
            <Td wide={true} label={t("queue.instructions")}>
              <span class="whitespace-pre-line text-zinc-600">{r.instructions}</span>
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** A queue as shown: with everyone's, how to name whose queue each entry (and rule) is in. */
type Shown = QueueOut & { whose?: ((id: string) => string) | undefined };

interface ViewProps {
  space: SpaceOut;
  q: Shown;
  mine: boolean;
}

/**
 * Apart from the queue: items reviewed of their own accord (no rule or request asks for them) whose review went
 * stale since. Not owed, but worth a look.
 */
function Voluntary({ space, q }: ViewProps) {
  if (q.voluntary.length === 0) {
    return null;
  }
  return (
    <section class="mt-8">
      <h2 class="mb-1 text-sm font-medium text-zinc-900">{t("queue.voluntary", { count: q.voluntary_total })}</h2>
      <p class="mb-2 text-sm text-zinc-500">{t("queue.voluntaryHint")}</p>
      <ul class="divide-y divide-zinc-200 rounded-lg border border-zinc-200 bg-white">
        {q.voluntary.map((e) => (
          <Entry key={`${e.item.id}:${e.reviewer}`} space={space} e={e} q={q} reason={false} />
        ))}
      </ul>
    </section>
  );
}

/** How much of a comment the queue shows. */
const SNIPPET = 160;

/** What a comment is on: its item, rule or entity (by title), or the space's channel. */
function subjectOf(c: QueueOut["comments"][number]): string {
  return c.subject_title || c.item || c.rule || c.entity
    ? t("queue.commentOn", { title: titleOf({ title: c.subject_title }) })
    : t("queue.commentInChannel");
}

/** Comments for you, new: mentioning you, answering you, or about your reviews; each links its item's conversation. */
function ForYou({ space, q }: ViewProps) {
  if (q.comments.length === 0) {
    return null;
  }
  return (
    <section class="mb-8">
      <h2 class="mb-1 text-sm font-medium text-zinc-900">{t("queue.comments")}</h2>
      <p class="mb-2 text-sm text-zinc-500">{t("queue.commentsHint")}</p>
      <ul class="divide-y divide-zinc-200 rounded-lg border border-zinc-200 bg-white">
        {q.comments.map((c) => {
          const text = clip(plain(c.body ?? ""), SNIPPET);
          return (
            <li key={c.id} class="flex flex-col gap-1 px-4 py-3 text-sm">
              <span class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span class="font-medium text-zinc-900">{displayName(c.author, c.author_name)}</span>
                <Link href={spaceUrl(space, conversationPath(c))} class={LINK}>
                  {subjectOf(c)}
                </Link>
                <span class="text-xs text-zinc-500">{formatRelative(c.created_at)}</span>
              </span>
              {text ? <span class="text-zinc-700">{text}</span> : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Waiting(p: ViewProps) {
  return (
    <>
      <ForYou {...p} />
      <Owed {...p} />
      <Voluntary {...p} />
    </>
  );
}

function Owed({ space, q, mine }: ViewProps) {
  if (q.data.length === 0) {
    return (
      <EmptyState title={mine ? t("queue.empty") : t("queue.emptyOther")}>
        {q.rules.length === 0 ? t("queue.noRulesHint") : null}
      </EmptyState>
    );
  }
  return (
    <>
      {q.total > q.data.length ? (
        <p class="mb-2 text-sm text-zinc-500">{t("queue.showingOldest", { shown: q.data.length, total: q.total })}</p>
      ) : null}
      <ul class="divide-y divide-zinc-200 rounded-lg border border-zinc-200 bg-white">
        {q.data.map((e) => (
          <Entry key={`${e.item.id}:${e.reviewer}`} space={space} e={e} q={q} />
        ))}
      </ul>
    </>
  );
}

/** Everyone's rules, by reviewer (in the order their first rule was made). */
function ByReviewer({ space, q, whose }: { space: SpaceOut; q: QueueOut; whose: (id: string) => string }) {
  const groups = Map.groupBy(q.rules, (r) => r.reviewer);
  return (
    <div class="flex flex-col gap-6">
      {[...groups].map(([reviewer, rules]) => (
        <section key={reviewer}>
          <h2 class="mb-2 text-sm font-medium text-zinc-900">{whose(reviewer)}</h2>
          <Rules space={space} rules={rules} />
        </section>
      ))}
    </div>
  );
}

function RulesTab({ space, q, mine }: ViewProps) {
  if (q.rules.length === 0) {
    const title = mine ? t("queue.noRules") : q.whose ? t("queue.noRulesEveryone") : t("queue.noRulesOther");
    return <EmptyState title={title}>{t("queue.noRulesHint")}</EmptyState>;
  }
  return q.whose ? <ByReviewer space={space} q={q} whose={q.whose} /> : <Rules space={space} rules={q.rules} />;
}

export function Queue({ space }: PageProps) {
  const reviewer = location.value.searchParams.get("reviewer") ?? me.value?.id ?? "";
  const mine = reviewer === me.value?.id;
  const everyone = reviewer === EVERYONE;
  const rulesTab = tabParam(["waiting", "rules"]) === "rules";
  // The tabs keep whose queue it is.
  const base = spaceUrl(space, "/queue");
  const all = useResource(`queues:${space.id}`, (signal) =>
    api.get<Queues>(spaceApi(space, "/queues"), undefined, signal),
  );
  const whose = useReviewerName(all.data?.data ?? []);
  const queue = useResource(`queue:${space.id}:${reviewer}`, (signal) =>
    api.get<QueueOut>(
      spaceApi(space, "/queue"),
      everyone ? { limit: QUEUE_LIMIT, everyone: true } : { limit: QUEUE_LIMIT, reviewer: mine ? null : reviewer },
      signal,
    ),
  );
  return (
    <>
      <PageHeader title={t("queue.title")}>
        <ReviewerPicker all={all} reviewer={reviewer} />
      </PageHeader>
      <Async r={queue}>
        {(got) => {
          const q: Shown = everyone ? { ...got, whose } : got;
          return (
            <>
              <TabNav
                label={t("queue.sections")}
                tabs={[
                  {
                    href: tabHref(base, null, true),
                    label: t("queue.waitingTab", { count: q.total }),
                    current: !rulesTab,
                  },
                  {
                    href: tabHref(base, "rules", true),
                    label: t("queue.rulesTab", { count: q.rules.length }),
                    current: rulesTab,
                  },
                ]}
              />
              {rulesTab ? <RulesTab space={space} q={q} mine={mine} /> : <Waiting space={space} q={q} mine={mine} />}
            </>
          );
        }}
      </Async>
    </>
  );
}
